import React, { useState, useEffect, useContext } from 'react';
import TableSortLabel from '@mui/material/TableSortLabel';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Container,
  CircularProgress,
  IconButton,
  Box,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AuthContext from '../contexts/AuthContext';

export default function Products() {
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
  const { tokens } = useContext(AuthContext);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // List of available requisition form templates
  const [formTemplates, setFormTemplates] = useState([]);
  // List of available collection forms for products
  const [collectionForms, setCollectionForms] = useState([]);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price_in_cents: 0,
    requisition_form_id: '',
    dynamic_collection_form_id: ''
  });
  // State for viewing collection form details
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [selectedFormDetail, setSelectedFormDetail] = useState(null);
  const [loadingFormDetail, setLoadingFormDetail] = useState(false);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Fetch available form templates for requisition
  const fetchFormTemplates = async () => {
    if (!tokens?.id_token) return;
    try {
      const res = await fetch(`${apiBase}/form-templates`, {
        headers: { Authorization: `Bearer ${tokens.id_token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch form templates');
      const data = await res.json();
      setFormTemplates(data.items || []);
    } catch (err) {
      console.error('Error fetching form templates:', err);
    }
  };
  // Fetch available collection forms for products
  const fetchCollectionForms = async () => {
    if (!tokens?.id_token) return;
    try {
      const res = await fetch(`${apiBase}/dynamic-forms?is_collection_form=true`, {
        headers: { Authorization: `Bearer ${tokens.id_token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch collection forms');
      const data = await res.json();
      setCollectionForms(data.items || []);
    } catch (err) {
      console.error('Error fetching collection forms:', err);
    }
  };

  const sortedProducts = products.slice().sort((a, b) => {
    let aVal = a[orderBy] ?? '';
    let bVal = b[orderBy] ?? '';
    if (typeof aVal === 'string') {
      const cmp = aVal.localeCompare(bVal);
      return order === 'asc' ? cmp : -cmp;
    }
    if (aVal < bVal) return order === 'asc' ? -1 : 1;
    if (aVal > bVal) return order === 'asc' ? 1 : -1;
    return 0;
  });

  const fetchProducts = async () => {
    if (!tokens?.id_token) return;
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/products`, {
        headers: { Authorization: `Bearer ${tokens.id_token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch products');
      const data = await res.json();
      setProducts(data.items || []);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchFormTemplates();
    fetchCollectionForms();
  }, [tokens]);

  const handleOpenCreate = () => {
    setEditingProduct(null);
    setFormData({ name: '', description: '', price_in_cents: 0, requisition_form_id: '', dynamic_collection_form_id: '' });
    setDialogOpen(true);
  };

  const handleOpenEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description || '',
      price_in_cents: product.price_in_cents,
      requisition_form_id: product.requisition_form_id || '',
      dynamic_collection_form_id: product.dynamic_collection_form_id || ''
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProduct(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const method = editingProduct ? 'PUT' : 'POST';
      const url = editingProduct
        ? `${apiBase}/products/${editingProduct.id}`
        : `${apiBase}/products`;
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens.id_token}`
        },
        body: JSON.stringify(formData)
      });
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || 'Failed to save product');
      }
      await fetchProducts();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving product:', err);
    } finally {
      setLoading(false);
    }
  };
  // Open requisition form PDF in new tab with auth
  const handleOpenForm = async (templateId) => {
    if (!tokens?.id_token) return;
    try {
      const res = await fetch(`${apiBase}/form-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${tokens.id_token}` }
      });
      if (!res.ok) throw new Error('Failed to download form');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      console.error('Error opening form:', err);
    }
  };
  // Open collection form details dialog
  const handleOpenCollectionFormDetail = async (formId) => {
    setFormDialogOpen(true);
    setLoadingFormDetail(true);
    try {
      const res = await fetch(`${apiBase}/dynamic-forms/${formId}`, {
        headers: { Authorization: `Bearer ${tokens.id_token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch collection form detail');
      const data = await res.json();
      setSelectedFormDetail(data);
    } catch (err) {
      console.error('Error fetching collection form detail:', err);
    } finally {
      setLoadingFormDetail(false);
    }
  };
  const handleCloseFormDialog = () => {
    setFormDialogOpen(false);
    setSelectedFormDetail(null);
  };

  if (loading && !dialogOpen) {
    return (
      <Container disableGutters sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container disableGutters>
        <Typography color="error" variant="h6" sx={{ mt: 4 }}>
          Error: {error}
        </Typography>
      </Container>
    );
  }

  return (
    <Container disableGutters sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4" component="h1">
          Products
        </Typography>
        <Button variant="contained" onClick={handleOpenCreate}>
          Add Product
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === 'id' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'id'}
                  direction={orderBy === 'id' ? order : 'asc'}
                  onClick={() => handleRequestSort('id')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'name' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleRequestSort('name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>Description</TableCell>
              <TableCell sortDirection={orderBy === 'price_in_cents' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'price_in_cents'}
                  direction={orderBy === 'price_in_cents' ? order : 'asc'}
                  onClick={() => handleRequestSort('price_in_cents')}
                >
                  Price
                </TableSortLabel>
              </TableCell>
              <TableCell>Form</TableCell>
              <TableCell>Collection Form</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedProducts.map((product) => {
              const shortId = String(product.id).split('-')[0];
              return (
                <TableRow key={product.id}>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Tooltip title={product.id} arrow>
                        <Typography variant="body2" sx={{ cursor: 'default' }}>
                          {shortId}
                        </Typography>
                      </Tooltip>
                      <Tooltip title="Copy full ID" arrow>
                        <IconButton
                          aria-label="copy full id"
                          size="small"
                          onClick={() => navigator.clipboard.writeText(product.id)}
                          sx={{ ml: 0.5 }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>{product.name}</TableCell>
                  <TableCell>{product.description}</TableCell>
                  <TableCell>{(product.price_in_cents / 100).toFixed(2)}</TableCell>
                  <TableCell>
                    {product.requisition_form_id ? (
                      <IconButton
                        size="small"
                        aria-label="open requisition form"
                        onClick={() => handleOpenForm(product.requisition_form_id)}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    {product.dynamic_collection_form_id ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                          {collectionForms.find(f => f.id === product.dynamic_collection_form_id)?.name || product.dynamic_collection_form_id}
                        </Typography>
                        <IconButton
                          size="small"
                          aria-label="view collection form"
                          onClick={() => handleOpenCollectionFormDetail(product.dynamic_collection_form_id)}
                          sx={{ ml: 0.5 }}
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <IconButton aria-label="edit" size="small" onClick={() => handleOpenEdit(product)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>{editingProduct ? 'Edit Product' : 'Add Product'}</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
            />
            <TextField
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
            />
            <TextField
              label="Price (cents)"
              name="price_in_cents"
              type="number"
              value={formData.price_in_cents}
              onChange={handleChange}
            />
            <TextField
              select
              label="Requisition Form"
              name="requisition_form_id"
              value={formData.requisition_form_id}
              onChange={handleChange}
            >
              <MenuItem value="">None</MenuItem>
              {formTemplates.map((tpl) => (
                <MenuItem key={tpl.id} value={tpl.id}>
                  {tpl.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Collection Form"
              name="dynamic_collection_form_id"
              value={formData.dynamic_collection_form_id}
              onChange={handleChange}
            >
              <MenuItem value="">None</MenuItem>
              {collectionForms.map((form) => (
                <MenuItem key={form.id} value={form.id}>
                  {form.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Collection Form Details Dialog */}
      <Dialog open={formDialogOpen} onClose={handleCloseFormDialog} fullWidth maxWidth="md">
        <DialogTitle>Collection Form Details</DialogTitle>
        <DialogContent dividers>
          {loadingFormDetail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedFormDetail ? (
            <>
              <Typography variant="h6">{selectedFormDetail.name}</Typography>
              {selectedFormDetail.description && (
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {selectedFormDetail.description}
                </Typography>
              )}
              {selectedFormDetail.groups.map((group) => (
                <Accordion key={group.id}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>{group.name}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {group.description && (
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        {group.description}
                      </Typography>
                    )}
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Required</TableCell>
                          <TableCell>Order</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.fields.map((field) => (
                          <TableRow key={field.id}>
                            <TableCell>{field.name}</TableCell>
                            <TableCell>{field.type}</TableCell>
                            <TableCell>{field.required ? 'Yes' : 'No'}</TableCell>
                            <TableCell>{field.order}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </AccordionDetails>
                </Accordion>
              ))}
            </>
          ) : (
            <Typography>No details available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFormDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}